import type { FC, RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { groupValuesByLabel } from "@locators/components/shared/components/StoreLocatorCard/components/StoreLocatorAvailableProducts/utils";
import {
  calculateAvailableWidth,
  calculateVisibleGroupCount,
  calculateWidthPerGroup,
  DEFAULT_STICK_KEY,
} from "@locators/components/shared/components/StoreLocatorCardV2/components/StoreLocatorAvailableProducts/utils";

import styles from "./StoreLocatorProductAttributeValues.module.scss";

type ValueWithLabel = { value: string | number; label?: string };

type StoreLocatorStickAttributeValuesProps = {
  values: ValueWithLabel[];
  titleRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
};

export const StoreLocatorStickAttributeValues: FC<StoreLocatorStickAttributeValuesProps> = ({
  values,
  titleRef,
  containerRef,
}) => {
  const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  const groupedByLabel = useMemo(() => groupValuesByLabel(values), [values]);
  const groupKeys = useMemo(() => Object.keys(groupedByLabel), [groupedByLabel]);

  useEffect(() => {
    const update = () => {
      const [fullWidth, titleWidth] = [containerRef, titleRef].map((ref) => ref.current?.offsetWidth ?? 0);
      const availableWidth = calculateAvailableWidth(fullWidth, titleWidth);
      if (availableWidth <= 0) return;

      const widthPerGroup = calculateWidthPerGroup(availableWidth, groupKeys.length);
      const newVisibleCounts = calculateVisibleGroupCount(groupedByLabel, itemRefs, widthPerGroup);

      setVisibleCounts(newVisibleCounts);
      if (!isInitialized) setIsInitialized(true);
    };

    update();

    const observer = new ResizeObserver(update);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [values, groupedByLabel, titleRef, containerRef, isInitialized, groupKeys]);

  const getRefIndex = (groupIdx: number, valueIdx?: number): number => {
    let index = 0;
    for (let i = 0; i < groupIdx; i++) {
      const label = groupKeys[i];
      if (label) index++;
      index += groupedByLabel[label].length;
    }
    if (valueIdx !== undefined) {
      const currentLabel = groupKeys[groupIdx];
      if (currentLabel) index++;
      index += valueIdx;
    }
    return index;
  };

  return (
    <div className={styles.productAttributeValues} data-testid="storeLocatorAttributeValues">
      <div className={styles.productAttributeValuesList}>
        {groupKeys.map((label, groupIdx) => {
          const groupValues = groupedByLabel[label];
          const visibleCount = visibleCounts[label] ?? groupValues.length;
          const hiddenCount = groupValues.length - visibleCount;
          const labelRefIndex = getRefIndex(groupIdx);

          return (
            <span key={label || `${DEFAULT_STICK_KEY}-${groupIdx}`} className={styles.productAttributeValuesGroup}>
              {label && (
                <span
                  className={styles.productSticksLabel}
                  ref={(el) => {
                    itemRefs.current[labelRefIndex] = el;
                  }}
                >
                  {label}:{" "}
                </span>
              )}
              {groupValues.map((value, idx) => {
                const isVisible = idx < visibleCount;
                const refIndex = getRefIndex(groupIdx, idx);

                if (!isVisible && isInitialized) return null;

                return (
                  <span
                    className={styles.productAttributesAdditional}
                    key={`${label}-${value}-${idx}`}
                    ref={(el) => {
                      itemRefs.current[refIndex] = el;
                    }}
                  >
                    {value}
                    {isVisible && idx < visibleCount - 1 && " · "}
                  </span>
                );
              })}
              {hiddenCount > 0 && (
                <span className={styles.productAttributeValuesCount} data-testid="attributeValuesCount">
                  +{hiddenCount}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
};
